import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "ui/Button";
import TextEditor from "components/TextEditor";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { toActorFromChatActor } from "util/actorIdentity";
import type { MediaSlot } from "hooks/useMediaManager";
import type { PollDraft } from "types/poll";
import type { ChatAttachment, ChatMessage } from "types/chat";
import { chatApi } from "lib/api/chatApi";
import { useReplyInThreadMutation, useSendChatMessageMutation } from "hooks/queries/useChatQueries";
import { useSocket } from "hooks/useSocket";

interface ChatComposerProps {
  conversationPublicId: string;
  replyToMessage?: ChatMessage | null;
  threadRootMessagePublicId?: string | null;
  onSent?: () => void;
  onCancelReply?: () => void;
  placeholder?: string;
}

const ChatComposer: React.FC<ChatComposerProps> = ({
  conversationPublicId,
  replyToMessage = null,
  threadRootMessagePublicId = null,
  onSent,
  onCancelReply,
  placeholder = "Message",
}) => {
  const [bodyHtml, setBodyHtml] = useState<string>("");
  const [mediaSlots, setMediaSlots] = useState<MediaSlot[]>([]);
  const [pollDraft, setPollDraft] = useState<PollDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [editorKey, setEditorKey] = useState<number>(0);
  const sendMutation = useSendChatMessageMutation(conversationPublicId);
  const replyMutation = useReplyInThreadMutation(threadRootMessagePublicId || "", conversationPublicId);
  const socketContext = useSocket();

  const activeMutation = threadRootMessagePublicId ? replyMutation : sendMutation;
  const hasContent = useMemo(() => {
    const trimmed = bodyHtml.trim();
    return trimmed.length > 0 || mediaSlots.length > 0 || Boolean(pollDraft?.statement.trim());
  }, [bodyHtml, mediaSlots.length, pollDraft]);

  useEffect(() => {
    if (!conversationPublicId) {
      return;
    }
    if (!bodyHtml.trim()) {
      socketContext?.emitTypingStop?.(conversationPublicId);
      return;
    }
    socketContext?.emitTypingStart?.(conversationPublicId);
    const timeout = window.setTimeout(() => {
      socketContext?.emitTypingStop?.(conversationPublicId);
    }, 1500);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [bodyHtml, conversationPublicId, socketContext]);

  const uploadAttachments = async (): Promise<ChatAttachment[]> => {
    if (mediaSlots.length === 0) {
      return [];
    }

    const presign = await chatApi.createMediaPresignUrls(conversationPublicId, {
      files: mediaSlots.map((slot) => ({
        name: slot.file.name,
        type: slot.file.type,
        size: slot.file.size,
        order: slot.order,
      })),
    });

    await Promise.all(presign.uploads.map(async (upload, index) => {
      const slot = mediaSlots[index];
      const response = await fetch(upload.uploadUrl, {
        method: upload.method,
        headers: upload.headers,
        body: slot.file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed for ${slot.file.name}`);
      }
    }));

    const finalized = await chatApi.finalizeMediaUpload(conversationPublicId, {
      mediaFiles: presign.uploads.map((upload) => ({
        key: upload.key,
        media_type: upload.type,
        mime_type: upload.mimetype,
        file_name: upload.originalName,
        file_size: upload.size,
        width: null,
        height: null,
        duration_ms: null,
        sort_order: upload.order,
      })),
    });

    return finalized.mediaFiles;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!hasContent) {
      return;
    }

    setError(null);
    setIsUploading(true);
    try {
      const attachments = await uploadAttachments();
      if (threadRootMessagePublicId) {
        await replyMutation.mutateAsync({
          body_html: bodyHtml,
          attachments,
          poll: pollDraft,
          reply_to_message_public_id: replyToMessage?.public_id || threadRootMessagePublicId,
          thread_root_message_public_id: threadRootMessagePublicId,
        });
      } else {
        await sendMutation.mutateAsync({
          body_html: bodyHtml,
          attachments,
          poll: pollDraft,
          reply_to_message_public_id: replyToMessage?.public_id || null,
        });
      }

      setBodyHtml("");
      setMediaSlots([]);
      setPollDraft(null);
      setEditorKey((previous) => previous + 1);
      socketContext?.emitTypingStop?.(conversationPublicId);
      onSent?.();
    } catch (submissionError) {
      const message = submissionError instanceof Error ? submissionError.message : "Unable to send message.";
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-eoe-border-main dark:border-eoe-dark-border-main bg-transparent p-3">
      {replyToMessage ? (
        <div className="flex items-start justify-between gap-3 rounded-xl bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-3">
          {/*
           * Reply preview is in-thread context: open the actor's
           * profile (not a story) and skip the ring, matching the
           * `ChatMessage` bubble treatment.
           */}
          <ActorAvatar
            actor={toActorFromChatActor(replyToMessage.sender)}
            size={ACTOR_AVATAR_SIZES.message}
            clickTo="profile"
            showRing={false}
          />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
              Replying to
            </div>
            <div className="truncate text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
              {replyToMessage.sender?.display_name || replyToMessage.sender?.username || "Message"}
            </div>
            <div className="line-clamp-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {replyToMessage.body || "Media message"}
            </div>
          </div>
          {onCancelReply ? (
            <button
              type="button"
              onClick={onCancelReply}
              className="rounded-full p-1 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover"
              aria-label="Cancel reply"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      ) : null}

      <TextEditor
        key={editorKey}
        value={bodyHtml}
        onChange={setBodyHtml}
        editorType="comment"
        action="add"
        placeholder={placeholder}
        pollDraft={pollDraft}
        onPollDraftChange={setPollDraft}
        onCommentMediaManagerChange={(mediaState) => {
          if (Array.isArray(mediaState)) {
            setMediaSlots(mediaState);
          }
        }}
      />

      {error ? (
        <div className="text-sm text-warning-red dark:text-dark-warning-red">{error}</div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={handleSubmit}
          disabled={!hasContent || isUploading || activeMutation.isPending}
        >
          {isUploading || activeMutation.isPending ? "Sending..." : "Send"}
        </Button>
      </div>
    </div>
  );
};

export default ChatComposer;
